import { addCalendarDays } from "../services/journeyTime";
import { useState, useEffect } from "react";
import { BedDouble, ChevronDown, ChevronUp } from "../data/design-tokens";
import { AnimatePresence, motion } from "framer-motion";

// ─── Affiliate Configuration ────────────────────────────────────────────

const STAY22_AID = "modthryth";

// ─── URL Generators (consumed by SearchPanel) ───────────────────────────

export function getTrainlineUrl(fromCity: string, toCity: string, date?: string): string {
  const params = new URLSearchParams({ origin: fromCity, destination: toCity });
  if (date) params.set("outwardDate", date);
  return `https://www.thetrainline.com/book/results?${params.toString()}`;
}

export function getOmioUrl(fromCity: string, toCity: string, date?: string): string {
  const params = new URLSearchParams({ from: fromCity, to: toCity });
  if (date) params.set("date", date);
  return `https://www.omio.com/search?${params.toString()}`;
}

// ─── Stay22 Embed URL Builder ───────────────────────────────────────────

function buildStay22EmbedUrl(
  lat: number,
  lng: number,
  city: string,
  checkinDate?: string,
  nights = 1,
): string {
  const params = new URLSearchParams({
    aid: STAY22_AID,
    lat: lat.toString(),
    lng: lng.toString(),
    maincolor: "6366f1",
    campaign: city.toLowerCase().replaceAll(/\s+/g, "-"),
  });
  if (checkinDate) {
    params.set("checkin", checkinDate);
    params.set("checkout", addCalendarDays(checkinDate, nights));
  }
  return `https://www.stay22.com/embed/gm?${params.toString()}`;
}

// ─── Stay22 Embedded Map Widget ─────────────────────────────────────────

interface Stay22EmbedProps {
  latitude: number;
  longitude: number;
  city: string;
  checkinDate?: string;
  nights?: number;
  isOvernightStop: boolean;
}

const EMBED_HEIGHT = 350;

export function Stay22Embed({
  latitude,
  longitude,
  city,
  checkinDate,
  nights = 1,
  isOvernightStop,
}: Readonly<Stay22EmbedProps>) {
  const [expanded, setExpanded] = useState(isOvernightStop);

  // Auto-expand when overnight toggled on
  useEffect(() => {
    if (isOvernightStop) setExpanded(true);
  }, [isOvernightStop]);

  const collapsedClasses = isOvernightStop
    ? "border-purple-300 bg-purple-50/60 text-purple-700 hover:bg-purple-50"
    : "border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100";
  const collapsedLabel = isOvernightStop ? "Compare hotels & hostels" : "Staying the night?";

  return (
    <div className="mt-1">
      {/* Toggle header */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border transition-all text-[12px] ${
          expanded
            ? "border-indigo-300 bg-indigo-50 text-indigo-700"
            : collapsedClasses
        }`}
      >
        <BedDouble className="size-3.5 flex-shrink-0" />
        <span className="flex-1 text-left" style={{ fontWeight: 500 }}>
          {expanded ? "Where to Stay" : collapsedLabel}
        </span>
        {expanded ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
      </button>

      {/* Collapsible iframe */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="stay22-iframe"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: EMBED_HEIGHT, opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            style={{ overflow: "hidden", borderRadius: "0 0 8px 8px" }}
          >
            <iframe
              src={buildStay22EmbedUrl(latitude, longitude, city, checkinDate, nights)}
              title={`Stay22 hotels near ${city}`}
              width="100%"
              height={EMBED_HEIGHT}
              style={{ border: 0, display: "block" }}
              loading="lazy"
              allow="geolocation"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
